import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./api";

export type RecommendationUserState =
  | "pending"
  | "dismissed"
  | "saved"
  | "snoozed"
  | "registered";

// Канон один на весь фронт — см. domain/discipline.ts
import type { RaceDiscipline } from "./domain/discipline";
export type { RaceDiscipline };
export type RaceTier = "wmm" | "major_local" | "series" | "classic";

export type EventCard = {
  race_id: number;
  slug: string;
  name: Record<string, string>;
  distance_km: number;
  discipline: RaceDiscipline;
  tier: RaceTier;
  series_slug: string | null;
  country: string | null;
  city: string | null;
  next_race_date: string | null;
  typical_month: number | null;
  registration_url: string | null;
  registration_status: string | null;
  registration_opens_at: string | null;
  registration_closes_at: string | null;
  is_lottery: boolean;
  score: number;
  factor_tier: number;
  factor_geo: number;
  factor_discipline: number;
  factor_goal: number;
  factor_season: number;
  factor_volume: number;
  distance_to_athlete_km: number | null;
  achievability_pct: number | null;
  achievability_label: string | null;
  /**
   * Canon identity state class (v153 P6.18 / v154.A). Replaces
   * legacy achievability_tone — dropped in v154 PART2 G1.
   */
  achievability_state_class: "achievable" | "borderline" | "stretch" | null;
  // Sess 07 — UX evolution: progression hints + coach trust marker.
  coach_approved?: boolean;
  next_step_suggestion?: { distance: string; weeks: number } | null;
  series_next_step?: { race_id: number; name_i18n: Record<string, string>; distance_km: number } | null;
  // v2 additions: confidence interval + bottleneck explainer.
  achievability_pct_low: number | null;
  achievability_pct_high: number | null;
  bottleneck_name: string | null;
  bottleneck_explanation: string | null;
  user_state: RecommendationUserState | null;
  estimated_month: number | null;
};

export type AppliedFilterChip = {
  key: string;
  label: string;
  value: string;
};

export type RadiusBucket = {
  km: number;
  count: number;
};

export type RecommendationsResponse = {
  items: EventCard[];
  athlete_has_coords: boolean;
  athlete_has_active_goal: boolean;
  saved_count: number;
  smart_match_active: boolean;
  applied_filters: AppliedFilterChip[];
  radius_buckets: RadiusBucket[];
};

export type Achievability = {
  applicable: boolean;
  has_data: boolean;
  has_target: boolean;
  probability_pct: number | null;
  label: string | null;
  /**
   * Canon identity state class (v153 P6.18). Replaces legacy
   * label_tone — dropped in v154 PART2 G1.
   */
  state_class: "achievable" | "borderline" | "stretch" | null;
  current_vdot: number | null;
  target_vdot: number | null;
  predicted_today_seconds: number | null;
  predicted_low_seconds: number | null;
  predicted_high_seconds: number | null;
  weeks_to_fit: number | null;
  weeks_available: number | null;
  growth_rate_per_week: number | null;
  growth_confidence: string | null;
  growth_source: string | null;
  factor_base: number;
  factor_form: number;
  factor_evidence: number;
  factor_time: number;
  notes: string[];
  blockers: string[];
};

export type PlanWeek = {
  week_index: number;
  weeks_to_race: number;
  long_run_km: number;
  kind: "build" | "recovery" | "peak" | "taper" | "race";
  note: string;
};

export type Milestone = {
  code: string;
  title: string;
  description: string;
  target_distance_km: number | null;
  target_time_seconds: number | null;
  is_completed: boolean;
  order: number;
  i18n_params?: Record<string, number | string>;
};

export type PlanPreview = {
  weeks_to_race: number | null;
  weekly_long_runs: PlanWeek[];
  milestones: Milestone[];
  note: string;
};

export type DegradedSuggestion = {
  title: string;
  description: string;
  distance_km: number | null;
  weeks_to_target: number;
  target_date: string;
  rationale: string;
  kind: "shorter" | "easier_pr" | "pushed_back" | "volume_block";
  i18n_key?: string;
  i18n_params?: Record<string, number | string>;
};

export type AchievabilityFactorBreakdown = {
  name: string;
  factor_value: number;
  confidence: "high" | "medium" | "low";
  tier: number;
  bottleneck_severity: number;
  explanation: string | null;
};

// B1 — comparable athletes benchmark.
export type ComparableBenchmark = {
  discipline: string;
  distance_km: number;
  our_finishers_n: number;
  our_median_sec: number | null;
  prior_median_sec: number;
  prior_citation: string;
  blended_median_sec: number;
  source_label: "literature_only" | "blended" | "mostly_ours";
  athlete_predicted_sec: number | null;
  athlete_percentile: number | null;   // % of comparable field beaten
};


export type EventDetail = {
  card: EventCard;
  achievability: Achievability;
  plan_preview: PlanPreview;
  degraded_suggestions: DegradedSuggestion[];
  achievability_factors: AchievabilityFactorBreakdown[] | null;
  achievability_weights_version: string | null;
  achievability_data_completeness: number | null;
};

export type RecommendationsFilters = {
  discipline?: RaceDiscipline;
  distance_band?: "local" | "continental" | "all";
  radius_km?: number;
  series?: string;
  horizon_days?: number;
  limit?: number;
  only_with_date?: boolean;
  smart_match?: boolean;
  distance_min_km?: number;
  distance_max_km?: number;
};

export function useEventRecommendations(filters: RecommendationsFilters = {}) {
  return useQuery<RecommendationsResponse>({
    queryKey: ["athlete-events-recs", filters],
    queryFn: async () => {
      const params: Record<string, string | number | boolean> = {};
      if (filters.discipline) params.discipline = filters.discipline;
      if (filters.distance_band) params.distance_band = filters.distance_band;
      if (filters.radius_km !== undefined) params.radius_km = filters.radius_km;
      if (filters.series) params.series = filters.series;
      if (filters.horizon_days) params.horizon_days = filters.horizon_days;
      if (filters.limit) params.limit = filters.limit;
      // only_with_date: server default is True. Pass explicitly only to disable.
      if (filters.only_with_date === false) params.only_with_date = false;
      else if (filters.only_with_date === true) params.only_with_date = true;
      if (filters.smart_match !== undefined)
        params.smart_match = filters.smart_match;
      if (filters.distance_min_km !== undefined)
        params.distance_min_km = filters.distance_min_km;
      if (filters.distance_max_km !== undefined)
        params.distance_max_km = filters.distance_max_km;
      const r = await api.get<RecommendationsResponse>(
        "/athlete/events/recommendations",
        { params },
      );
      return r.data;
    },
    staleTime: 5 * 60_000,
  });
}

export function useEventDetail(raceId: number | null, targetTimeSeconds?: number | null) {
  return useQuery<EventDetail>({
    queryKey: ["athlete-event-detail", raceId, targetTimeSeconds ?? null],
    queryFn: async () => {
      const params: Record<string, number> = {};
      if (targetTimeSeconds) params.target_time_seconds = targetTimeSeconds;
      const r = await api.get<EventDetail>(`/athlete/events/${raceId}`, { params });
      return r.data;
    },
    enabled: raceId !== null,
    staleTime: 60_000,
  });
}

export function useComparableBenchmark(raceId: number | null) {
  return useQuery<ComparableBenchmark | null>({
    queryKey: ["athlete-event-comparable", raceId],
    queryFn: async () => {
      const r = await api.get<ComparableBenchmark | null>(`/athlete/events/${raceId}/comparable`);
      return r.data;
    },
    enabled: raceId !== null,
    staleTime: 5 * 60 * 1000,
  });
}


export function useSavedEvents() {
  return useQuery<RecommendationsResponse>({
    queryKey: ["athlete-events-saved"],
    queryFn: async () => {
      const r = await api.get<RecommendationsResponse>("/athlete/events/saved");
      return r.data;
    },
    staleTime: 30_000,
  });
}

type StateOut = {
  race_id: number;
  state: RecommendationUserState;
  snoozed_until: string | null;
};

function useStateMutation(action: "dismiss" | "save" | "snooze" | "unsave") {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { raceId: number; days?: number }): Promise<StateOut> => {
      const url = `/athlete/events/${vars.raceId}/${action}`;
      const body = action === "snooze" ? { days: vars.days ?? 30 } : undefined;
      const r = await api.post<StateOut>(url, body);
      return r.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["athlete-events-recs"] });
      qc.invalidateQueries({ queryKey: ["athlete-events-saved"] });
      qc.invalidateQueries({ queryKey: ["athlete-event-detail"] });
    },
  });
}

export const useDismissEvent = () => useStateMutation("dismiss");
export const useSaveEvent = () => useStateMutation("save");
export const useSnoozeEvent = () => useStateMutation("snooze");
export const useUnsaveEvent = () => useStateMutation("unsave");

export type MakeGoalOut = {
  goal_id: number;
  target_time_seconds: number | null;
  race_distance: string;
  note: string;
};

export function useMakeGoalFromRace() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (raceId: number): Promise<MakeGoalOut> => {
      const r = await api.post<MakeGoalOut>(`/athlete/events/${raceId}/make-goal`);
      return r.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["athlete-events-recs"] });
      qc.invalidateQueries({ queryKey: ["goal-roadmap"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });
}

export function nameForLang(name: Record<string, string>, lang = "ru"): string {
  if (!name) return "забег";
  return name[lang] || name["ru"] || name["en"] || Object.values(name)[0] || "забег";
}

export function fmtTime(seconds: number | null | undefined): string {
  if (!seconds || seconds <= 0) return "—";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h) return `${h}:${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

/**
 * v154.A: canon state_class (identity) only. Legacy achievability_tone
 * fallback paths removed in v154 PART2 G1 (label_tone dropped from backend).
 * Identity tokens — state-achievable / state-borderline / state-stretch —
 * live in storica-tokens.css (added v153 P6.22).
 */
export function toneClasses(
  card: Pick<EventCard, "achievability_state_class"> | null | undefined,
): {
  text: string;
  bg: string;
  ring: string;
} {
  const sc = card?.achievability_state_class ?? null;
  if (sc === "achievable") {
    return {
      text: "text-[var(--state-achievable)]",
      bg: "bg-[var(--state-achievable-soft)]",
      ring: "ring-1 ring-[color:var(--state-achievable)]/30",
    };
  }
  if (sc === "borderline") {
    return {
      text: "text-[var(--state-borderline)]",
      bg: "bg-[var(--state-borderline-soft)]",
      ring: "ring-1 ring-[color:var(--state-borderline)]/30",
    };
  }
  if (sc === "stretch") {
    return {
      text: "text-[var(--state-stretch)]",
      bg: "bg-[var(--state-stretch-soft)]",
      ring: "ring-1 ring-[color:var(--state-stretch)]/30",
    };
  }
  return {
    text: "text-fg-muted",
    bg: "bg-bg-raised",
    ring: "ring-1 ring-bg-divider",
  };
}
